import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Image } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminGallery = () => {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    src: '',
    alt_sl: '',
    alt_en: '',
    category: 'nature'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/gallery`);
      setGallery(response.data);
    } catch (error) {
      // Gallery might be empty, that's ok
      setGallery([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({
      src: '',
      alt_sl: '',
      alt_en: '',
      category: 'nature'
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      src: item.src,
      alt_sl: item.alt_sl,
      alt_en: item.alt_en,
      category: item.category
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ali ste prepričani, da želite izbrisati to sliko?')) return;
    
    try {
      await axios.delete(`${API}/gallery/${id}`);
      toast.success('Slika izbrisana');
      fetchGallery();
    } catch (error) {
      toast.error('Napaka pri brisanju');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingItem) {
        await axios.put(`${API}/gallery/${editingItem.id}`, formData);
        toast.success('Slika posodobljena');
      } else {
        await axios.post(`${API}/gallery`, formData);
        toast.success('Slika dodana');
      }
      setIsDialogOpen(false);
      fetchGallery();
    } catch (error) {
      toast.error('Napaka pri shranjevanju');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-stone-400">{gallery.length} slik</p>
        <Button onClick={handleCreate} className="bg-emerald-600 hover:bg-emerald-500">
          <Plus className="w-5 h-5 mr-2" />
          Dodaj sliko
        </Button>
      </div>

      {/* Gallery Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {gallery.map((item) => (
          <Card key={item.id} className="bg-stone-800/50 border-stone-700 overflow-hidden group">
            <div className="relative aspect-[4/3]">
              <img
                src={item.src}
                alt={item.alt_sl}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => handleEdit(item)} className="text-white hover:bg-white/20">
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)} className="text-red-400 hover:bg-red-900/20">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="p-3">
              <p className="text-white text-sm truncate">{item.alt_sl}</p>
              <p className="text-stone-500 text-xs capitalize">{item.category === 'nature' ? 'Narava' : 'Divjad'}</p>
            </div>
          </Card>
        ))}

        {gallery.length === 0 && (
          <div className="col-span-full text-center py-12 text-stone-500">
            <Image className="w-12 h-12 mx-auto mb-4 opacity-50" />
            Ni slik. Kliknite "Dodaj sliko" za dodajanje.
          </div>
        )}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg bg-stone-900 border-stone-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingItem ? 'Uredi sliko' : 'Dodaj sliko'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-stone-300">URL slike</Label>
              <Input
                value={formData.src}
                onChange={(e) => setFormData({...formData, src: e.target.value})}
                className="bg-stone-800 border-stone-700 text-white"
                placeholder="https://..."
                required
              />
              {formData.src && (
                <img src={formData.src} alt="Preview" className="w-full h-32 object-cover rounded-md mt-2" />
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-stone-300">Opis (SL)</Label>
              <Input
                value={formData.alt_sl}
                onChange={(e) => setFormData({...formData, alt_sl: e.target.value})}
                className="bg-stone-800 border-stone-700 text-white"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-stone-300">Opis (EN)</Label>
              <Input
                value={formData.alt_en}
                onChange={(e) => setFormData({...formData, alt_en: e.target.value})}
                className="bg-stone-800 border-stone-700 text-white"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-stone-300">Kategorija</Label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full bg-stone-800 border border-stone-700 text-white rounded-md px-3 py-2"
              >
                <option value="nature">Narava</option>
                <option value="wildlife">Divjad</option>
              </select>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)} className="text-stone-400">
                Prekliči
              </Button>
              <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-500">
                {saving ? 'Shranjujem...' : 'Shrani'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGallery;
